/**
 * A student's group memberships, editable (#405).
 *
 * The student side of what `GroupStudents` does from the group side. It exists
 * because a student can now be enrolled in **several curriculums at once**, and
 * the group page can only answer "who is in this group" — putting one student
 * into a second group meant opening the other group and finding them there.
 *
 * Two different removals, deliberately not merged:
 *
 * - **Завершити** ends the enrolment (`is_active = false`). The student leaves
 *   the group's roster and their own switcher, while the membership row lives
 *   on, so everything they earned in that curriculum keeps its provenance. This
 *   is what happens when a course finishes.
 * - **Видалити** deletes the row. That is for "I put them in the wrong group" —
 *   nothing to preserve.
 *
 * Mutations are `IsSuperuser` server-side, so a teacher sees the list and no
 * controls (same rule as the mascot economy panel, #290).
 */
import React, { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useConfirm } from 'material-ui-confirm'
import { enqueueSnackbar } from 'notistack'

import {
  Autocomplete,
  Button,
  Chip,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import rpc, { buildRequest } from 'lib/rpc'

export default function StudentGroups({ user, isSuperuser, onChange }) {
  const confirm = useConfirm()
  const [groups, setGroups] = useState([])
  const [picked, setPicked] = useState(null)
  const [busy, setBusy] = useState(false)

  const memberships = (user && user.groups) || []
  const activeIds = memberships.filter((m) => m.is_active).map((m) => m.id)

  useEffect(() => {
    if (!isSuperuser) return
    rpc
      .post("", buildRequest("admin.groups.list", { show_all: true, per_page: 200 }))
      .then(({ data }) => {
        if (data.result) setGroups(data.result.data || [])
      })
  }, [isSuperuser])

  // Reporting through `onChange` rather than patching local state: the
  // membership list lives on the user record the parent owns, and a second
  // copy here would drift the moment anything else edits it.
  const run = (method, params, message, onSuccess) => {
    setBusy(true)
    rpc
      .post("", buildRequest(method, params))
      .then(({ data }) => {
        if (data.result && data.result.success) {
          enqueueSnackbar(message, { variant: "success" })
          if (onSuccess) onSuccess()
          if (onChange) onChange()
        } else {
          enqueueSnackbar((data.result && data.result.message) || "Не вдалося", {
            variant: "error",
          })
        }
      })
      .finally(() => setBusy(false))
  }

  const add = () => {
    if (!picked) return
    run(
      "admin.groups.add_student",
      { group_id: picked.id, student_id: user.id },
      `Додано до групи ${picked.name}`,
      // Cleared on success only: clearing it up front meant a failed add left
      // the admin with an empty picker and nothing to retry from.
      () => setPicked(null),
    )
  }

  const setActive = (membership, isActive) => {
    confirm({
      title: isActive ? "Відновити навчання?" : "Завершити навчання?",
      description: isActive
        ? `${user.first_name} ${user.last_name} знову з'явиться у групі «${membership.name}».`
        : `${user.first_name} ${user.last_name} більше не буде у групі «${membership.name}». `
          + "Історія оцінок збережеться.",
    })
      .then(() =>
        run(
          "admin.groups.set_student_active",
          { group_id: membership.id, student_id: user.id, is_active: isActive },
          isActive ? "Навчання відновлено" : "Навчання завершено",
        ),
      )
      .catch(() => {})
  }

  const remove = (membership) => {
    confirm({
      title: "Видалити з групи?",
      description:
        `Запис про навчання ${user.first_name} ${user.last_name} у групі «${membership.name}» `
        + "буде видалено. Це для випадку, коли учня додали помилково — якщо курс просто "
        + "завершився, обери «Завершити».",
    })
      .then(() =>
        run(
          "admin.groups.remove_student",
          { group_id: membership.id, student_id: user.id },
          "Видалено з групи",
        ),
      )
      .catch(() => {})
  }

  return (
    <Paper sx={{ mt: 2 }}>
      <Stack spacing={2} sx={{ p: 2 }}>
        <Typography variant="h6">Групи</Typography>

        {memberships.length === 0 && <Typography>Немає груп</Typography>}

        {memberships.map((membership) => (
          <Stack
            key={membership.id}
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Link
                to={`/groups/details/${membership.id}`}
                component={RouterLink}
                sx={{ overflowWrap: "anywhere" }}
              >
                <Typography>{membership.name}</Typography>
              </Link>
              {!membership.is_active && <Chip size="small" label="завершено" />}
            </Stack>
            {isSuperuser && (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  disabled={busy}
                  onClick={() => setActive(membership, !membership.is_active)}
                >
                  {membership.is_active ? "Завершити" : "Відновити"}
                </Button>
                <Button size="small" color="error" disabled={busy} onClick={() => remove(membership)}>
                  Видалити
                </Button>
              </Stack>
            )}
          </Stack>
        ))}

        {isSuperuser && (
          <>
            <Divider />
            <Stack direction="row" spacing={1} alignItems="center">
              <Autocomplete
                fullWidth
                size="small"
                value={picked}
                onChange={(event, value) => setPicked(value)}
                // Groups the student is already enrolled in are not offered.
                // An ended one stays in the list on purpose — re-adding it is
                // how an enrolment is resumed from this side.
                options={groups.filter((group) => !activeIds.includes(group.id))}
                getOptionLabel={(group) => group.name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(inputParams) => (
                  <TextField {...inputParams} label="Додати до групи" />
                )}
              />
              <Button variant="contained" disabled={!picked || busy} onClick={add}>
                Додати
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  )
}
